import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import {
  FiActivity, FiFilter, FiSearch, FiClock,
  FiUser, FiTruck, FiDollarSign, FiMessageSquare,
  FiHelpCircle, FiPackage, FiSettings
} from 'react-icons/fi';

function AdminActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadActivityLogs();
  }, [filter]);

  const loadActivityLogs = async () => {
    try {
      setLoading(true);
      // Récupérer les données complètes pour construire les logs
      const [statsResponse, usersResponse, movesResponse] = await Promise.all([
        api.getCompleteAppStats(),
        api.getAllUsers().catch(() => ({ data: [] })),
        api.getAllReservations().catch(() => ({ data: [] }))
      ]);

      // Construire les logs d'activité depuis les données
      const activityLogs = [];

      if (statsResponse && statsResponse.success) {
        // Activités récentes depuis les stats
        if (statsResponse.data.recentActivity) {
          statsResponse.data.recentActivity.forEach(activity => {
            activityLogs.push({
              id: activity.entityId + '-' + activity.date,
              type: activity.type,
              description: getActivityDescription(activity),
              icon: getActivityIcon(activity.type),
              date: activity.date,
              metadata: activity.metadata
            });
          });
        }

        // Nouvelles inscriptions (dernières 24h)
        if (usersResponse && usersResponse.data) {
          const recentUsers = usersResponse.data
            .filter(u => new Date(u.created_at) >= new Date(Date.now() - 24 * 60 * 60 * 1000))
            .slice(0, 10);
          
          recentUsers.forEach(user => {
            activityLogs.push({
              id: 'user-' + user.id,
              type: 'user_registered',
              description: `Nouvel utilisateur inscrit: ${user.first_name} ${user.last_name} (${user.role})`,
              icon: <FiUser className="w-5 h-5" />,
              date: user.created_at,
              metadata: user.role
            });
          });
        }
      }

      // Trier par date (plus récent en premier)
      activityLogs.sort((a, b) => new Date(b.date) - new Date(a.date));
      setLogs(activityLogs.slice(0, 100)); // Limiter à 100 logs
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registered':
        return <FiUser className="w-5 h-5" />;
      case 'quote_created':
        return <FiPackage className="w-5 h-5" />;
      case 'ticket_created':
        return <FiHelpCircle className="w-5 h-5" />;
      case 'payment':
        return <FiDollarSign className="w-5 h-5" />;
      case 'message':
        return <FiMessageSquare className="w-5 h-5" />;
      case 'move':
        return <FiTruck className="w-5 h-5" />;
      default:
        return <FiActivity className="w-5 h-5" />;
    }
  };

  const getActivityDescription = (activity) => {
    switch (activity.type) {
      case 'user_registered':
        return `Nouvel utilisateur inscrit: ${activity.entityName} (${activity.metadata})`;
      case 'quote_created':
        return `Nouveau devis créé: ${activity.entityName}`;
      case 'ticket_created':
        return `Nouveau ticket créé: ${activity.entityName}`;
      default:
        return activity.entityName;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'user_registered':
        return 'bg-blue-500/20 border-blue-500/30 text-blue-400';
      case 'quote_created':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
      case 'ticket_created':
        return 'bg-orange-500/20 border-orange-500/30 text-orange-400';
      case 'payment':
        return 'bg-green-500/20 border-green-500/30 text-green-400';
      case 'message':
        return 'bg-purple-500/20 border-purple-500/30 text-purple-400';
      case 'move':
        return 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-400';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLogs = logs.filter(log => {
    if (filter !== 'all' && log.type !== filter) return false;
    if (searchQuery && !log.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">Journal d'activité</h1>
          <p className="text-gray-400">Historique de toutes les activités sur la plateforme</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher dans les logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
          >
            <option value="all">Tous les types</option>
            <option value="user_registered">Inscriptions</option>
            <option value="quote_created">Devis</option>
            <option value="ticket_created">Tickets</option>
            <option value="payment">Paiements</option>
            <option value="message">Messages</option>
            <option value="move">Déménagements</option>
          </select>
        </div>
      </div>

      {/* Liste des logs */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement des logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-12 text-center">
            <FiActivity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Aucun log trouvé</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700 max-h-[600px] overflow-y-auto">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className="p-4 hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-2 rounded-lg border ${getActivityColor(log.type)}`}>
                    {log.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium">{log.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-400">
                      <span className="flex items-center">
                        <FiClock className="w-4 h-4 mr-1" />
                        {formatDate(log.date)}
                      </span>
                      {log.metadata && (
                        <span className="px-2 py-1 bg-gray-700 rounded text-xs">
                          {log.metadata}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Statistiques */}
      {!loading && logs.length > 0 && (
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4">Résumé</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-bold text-yellow-400">{logs.length}</p>
              <p className="text-gray-400 text-sm">Logs au total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-400">
                {logs.filter(l => l.type === 'user_registered').length}
              </p>
              <p className="text-gray-400 text-sm">Inscriptions</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-400">
                {logs.filter(l => l.type === 'quote_created').length}
              </p>
              <p className="text-gray-400 text-sm">Devis créés</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-400">
                {logs.filter(l => l.type === 'ticket_created').length}
              </p>
              <p className="text-gray-400 text-sm">Tickets créés</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminActivityLogs;


